import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import axios from "axios";
import { formatRole } from "../../utils/roleFormatter";
import BusinessSelector from "../../components/BusinessSelector";
import { getMyBusinesses } from "../../services/businessService";
import { useChecklist } from "../../contexts/ChecklistContext";
import AdminEmailChange from "../../components/AdminEmailChange";
import StaffImportModal from "../../components/StaffImportModal";
import "./shopStyles.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const Staff = () => {
  const navigate = useNavigate();
  const { markRequirementComplete } = useChecklist();
  const rolesList = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");
  const currentRole =
    localStorage.getItem("ogestyle_role_override") ||
    localStorage.getItem("ogestyle_role") ||
    "";
  const isR2Manager = currentRole === "R2" || rolesList.includes("R2");
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopId, setShopId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedStaffToMove, setSelectedStaffToMove] = useState(null);
  const [targetLocationId, setTargetLocationId] = useState("");
  const [editingStaff, setEditingStaff] = useState(null);
  const [commissionForm, setCommissionForm] = useState({
    commissionType: "none",
    commissionRate: 0,
    flatCommissionAmount: 0,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [addForm, setAddForm] = useState({
    email: "",
    professionalTypes: [], // Array of selected professional types
    primaryProfessionalType: "", // Primary type (must be in array)
    firstName: "",
    lastName: "",
    phone: "",
    locationId: "",
  });
  const [staffMetrics] = useState({});
  const [selectedStaffDetails, setSelectedStaffDetails] = useState(null);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  
  // Custom verification modal state
  const [verificationModal, setVerificationModal] = useState({
    isOpen: false,
    type: '', // 'commission-required', 'verify-confirm'
    staffMember: null,
  });
  const [businessCategories] = useState([]); // Business categories for current shop

  // Unassigned staff (imported but not assigned to a location)
  const [unassignedStaff, setUnassignedStaff] = useState([]);
  const [showUnassignedSection, setShowUnassignedSection] = useState(true);
  const [assigningStaffId, setAssigningStaffId] = useState(null);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [staffToAssign, setStaffToAssign] = useState(null);
  const [selectedLocationForAssignment, setSelectedLocationForAssignment] =
    useState("");

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Tab state for schedule overview
  const [activeTab, setActiveTab] = useState("team"); // 'team' or 'schedule'
  const [scheduleData, setScheduleData] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchBusinesses();
    fetchShopAndStaff();
    fetchProfessionalTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch professional types from backend
  const fetchProfessionalTypes = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/professional-types/public`,
      );
      setProfessionalTypes(response.data.professionalTypes || []);
    } catch (error) {
      // Fallback to empty array - form will show message
    }
  };

  // Filter and sort professional types based on business categories
  const getFilteredProfessionalTypes = () => {
    if (!professionalTypes.length) return [];

    // If no business categories, just sort alphabetically
    if (!businessCategories.length) {
      return [...professionalTypes].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }

    // Split into matching and non-matching
    const matching = [];
    const notMatching = [];

    professionalTypes.forEach((type) => {
      const typeCategories = type.businessCategories || [];
      const hasMatch = typeCategories.some((cat) =>
        businessCategories.includes(cat),
      );

      if (hasMatch) {
        matching.push(type);
      } else {
        notMatching.push(type);
      }
    });

    // Sort each group alphabetically
    matching.sort((a, b) => a.label.localeCompare(b.label));
    notMatching.sort((a, b) => a.label.localeCompare(b.label));

    // Return matching first, then non-matching
    return [...matching, ...notMatching];
  };

  // Phase 3: Fetch businesses
  const fetchBusinesses = async () => {
    try {
      const businessesData = await getMyBusinesses();
      setBusinesses(businessesData);
      if (businessesData.length === 1) {
        setSelectedBusinessId(businessesData[0]._id);
      }
    } catch (error) {
    }
  };

  const fetchShopAndStaff = async (selectedShopId = null) => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      // Fetch user's shops from backend (more secure than localStorage)
      const shopsResponse = await axios.get(
        `${API_BASE_URL}/api/shop/my-shops`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const userShops = Array.isArray(shopsResponse.data)
        ? shopsResponse.data
        : shopsResponse.data.shops || [];
      if (userShops.length === 0) {
        setLoading(false);
        return;
      }

      // Store all locations for multi-location features
      setLocations(userShops);

      // Use selected shop or first shop as default
      const currentShopId = selectedShopId || shopId || userShops[0]._id;
      setShopId(currentShopId);

      // Fetch staff for this shop
      const staffResponse = await axios.get(
        `${API_BASE_URL}/api/shops/${currentShopId}/staff`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setStaff(staffResponse.data.staff || []);

      // O1: fetch professionals added but not yet onboarded (invited, awaiting
      // signup/registration). Existing cancel/resend handlers already call
      // fetchShopAndStaff() expecting this refresh.
      try {
        const invitesResponse = await axios.get(
          `${API_BASE_URL}/api/shops/${currentShopId}/staff/invitations/pending`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // Only R3 professional invites belong on this page (R2 shown on the
        // Managers page, which reuses the same pending endpoint).
        setPendingInvitations(
          (invitesResponse.data.pendingInvitations || []).filter(
            (invite) => (invite.roleCode || invite.role) === "R3",
          ),
        );
      } catch {
        setPendingInvitations([]);
      }

      // Fetch imported professionals pending location assignment
      try {
        const unassignedResponse = await axios.get(
          `${API_BASE_URL}/api/shops/${currentShopId}/staff/unassigned`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setUnassignedStaff(unassignedResponse.data.unassignedStaff || []);
      } catch {
        setUnassignedStaff([]);
      }

      setLoading(false);
    } catch (error) {
      setUnassignedStaff([]);
      setPendingInvitations([]);
      setLoading(false);
    }
  };

  // Assign imported staff to selected location
  const handleAssignToLocation = async (staffId) => {
    // Open location selector modal
    setStaffToAssign(staffId);
    setSelectedLocationForAssignment(shopId || "");
    setShowLocationSelector(true);
  };

  // Confirm and execute staff assignment
  const confirmAssignToLocation = async () => {
    if (!staffToAssign || !selectedLocationForAssignment) return;

    const token = localStorage.getItem("ogestyle_token");
    setAssigningStaffId(staffToAssign);

    try {
      await axios.post(
        `${API_BASE_URL}/api/shops/${selectedLocationForAssignment}/staff/${staffToAssign}/assign-location`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert("Professional assigned to location successfully!");
      setShowLocationSelector(false);
      setStaffToAssign(null);
      await fetchShopAndStaff();
    } catch (error) {
      alert(
        error.response?.data?.message ||
          "Failed to assign professional to location",
      );
    } finally {
      setAssigningStaffId(null);
    }
  };

  // Fetch schedule overview for all staff
  const fetchScheduleOverview = useCallback(async () => {
    if (!shopId) return;

    setLoadingSchedule(true);
    const token = localStorage.getItem("ogestyle_token");

    try {
      // Fetch all staff schedules for this location
      const response = await axios.get(
        `${API_BASE_URL}/api/staff/schedule/overview`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { shopId },
        },
      );

      setScheduleData(response.data.schedules || []);
    } catch (error) {
      setScheduleData([]);
    } finally {
      setLoadingSchedule(false);
    }
  }, [shopId]);

  // Load schedule data when switching to schedule tab
  useEffect(() => {
    if (activeTab === "schedule" && shopId) {
      fetchScheduleOverview();
    }
  }, [activeTab, shopId, fetchScheduleOverview]);

  const openCommissionModal = (staffMember) => {
    // Don't allow editing commission for owners
    if (staffMember.isOwner) {
      alert(
        "Commission settings cannot be edited for shop owners. Owners automatically receive all shop earnings.",
      );
      return;
    }

    setEditingStaff(staffMember);
    setCommissionForm({
      commissionType: staffMember.commissionType || "none",
      commissionRate: staffMember.commissionRate || 0,
      flatCommissionAmount: staffMember.flatCommissionAmount || 0,
    });
  };

  const closeCommissionModal = () => {
    setEditingStaff(null);
    setCommissionForm({
      commissionType: "none",
      commissionRate: 0,
      flatCommissionAmount: 0,
    });
  };

  const handleCommissionUpdate = async () => {
    const token = localStorage.getItem("ogestyle_token");

    try {
      await axios.patch(
        `${API_BASE_URL}/api/shops/${shopId}/staff/${editingStaff._id}/commission`,
        commissionForm,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Refresh staff list
      await fetchShopAndStaff();
      closeCommissionModal();
      alert("Commission settings updated successfully!");
    } catch (error) {
      alert("Failed to update commission settings");
    }
  };

  const handleAddStaff = async () => {
    const token = localStorage.getItem("ogestyle_token");

    if (!addForm.email || !addForm.firstName || !addForm.lastName) {
      alert("Please fill in all required fields");
      return;
    }

    if (!addForm.professionalTypes || addForm.professionalTypes.length === 0) {
      alert("Please select at least one professional type");
      return;
    }

    if (addForm.professionalTypes.length > 5) {
      alert("Please select maximum 5 professional types");
      return;
    }

    if (!addForm.primaryProfessionalType) {
      alert("Please select a primary professional type");
      return;
    }

    if (!addForm.locationId) {
      alert("Please select a location");
      return;
    }

    try {
      // Phase 3: Use correct endpoint with shopId in body
      const response = await axios.post(
        `${API_BASE_URL}/api/staff/add`,
        {
          email: addForm.email,
          firstName: addForm.firstName,
          lastName: addForm.lastName,
          phone: addForm.phone,
          professionalTypes: addForm.professionalTypes,
          primaryProfessionalType: addForm.primaryProfessionalType,
          roleCode: "R3",
          shopId: addForm.locationId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const successMessage = `Professional added successfully! Password setup email sent to ${addForm.email}`;
      const warningMessage = response.data.typeMatchWarning;

      if (warningMessage) {
        alert(`${successMessage}\n\nWARNING: ${warningMessage}`);
      } else {
        alert(successMessage);
      }

      // Optimistically mark "Add professionals" requirement as complete
      markRequirementComplete("professional");

      setShowAddModal(false);
      setAddForm({
        email: "",
        professionalTypes: [],
        primaryProfessionalType: "",
        firstName: "",
        lastName: "",
        phone: "",
        locationId: "",
      });
      await fetchShopAndStaff();
    } catch (error) {
      // Show user-friendly error messages
      const errorData = error.response?.data;
      let errorMessage = "Failed to add professional";

      if (errorData?.error === "email_already_registered") {
        errorMessage =
          "This email address is already registered. Please use a different email address.";
      } else if (errorData?.error === "invitation_pending") {
        errorMessage =
          "An invitation is already pending for this email. Please wait for them to accept or resend the invitation.";
      } else if (errorData?.error === "validation_error") {
        errorMessage = errorData.message;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }

      alert(errorMessage);
    }
  };

  const handleMoveToLocation = async () => {
    if (!targetLocationId) {
      alert("Please select a location");
      return;
    }

    const token = localStorage.getItem("ogestyle_token");

    try {
      await axios.post(
        `${API_BASE_URL}/api/shops/${shopId}/staff/${selectedStaffToMove._id}/move-location`,
        { locationId: targetLocationId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(
        `${selectedStaffToMove.userId.firstName} has been moved to the selected location`,
      );
      setShowMoveModal(false);
      setSelectedStaffToMove(null);
      setTargetLocationId("");
      await fetchShopAndStaff();
    } catch (error) {
      alert(
        error.response?.data?.message ||
          "Failed to move professional to location",
      );
    }
  };

  const handleResendInvitation = async (staffMember) => {
    const token = localStorage.getItem("ogestyle_token");

    if (
      !window.confirm(`Resend invitation email to ${staffMember.userId.email}?`)
    ) {
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/shops/${shopId}/staff/${staffMember._id}/resend-invitation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(`Invitation resent to ${staffMember.userId.email}!`);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to resend invitation");
    }
  };

  const handleVerifyBackground = async (staffMember) => {
    // CRITICAL: Commission MUST be set before verification - NO EXCEPTIONS
    if (!staffMember.commissionType || staffMember.commissionType === "none") {
      setVerificationModal({
        isOpen: true,
        type: 'commission-required',
        staffMember: staffMember,
      });
      return;
    }

    // Commission is set, show final confirmation
    setVerificationModal({
      isOpen: true,
      type: 'verify-confirm',
      staffMember: staffMember,
    });
  };

  const proceedWithVerification = async (staffMember) => {
    const token = localStorage.getItem("ogestyle_token");

    try {
      await axios.patch(
        `${API_BASE_URL}/api/shops/${shopId}/staff/${staffMember._id}/verify-background`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setVerificationModal({ isOpen: false, type: '', staffMember: null });
      
      // Show success message
      const successDiv = document.createElement('div');
      successDiv.style.cssText = `
        position: fixed; top: 20px; right: 20px; z-index: 10000;
        padding: 16px 24px; background: #10b981; color: white;
        border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        font-weight: 500; animation: slideIn 0.3s ease-out;
      `;
      successDiv.textContent = `✓ ${staffMember.userId.firstName} ${staffMember.userId.lastName} has been verified!`;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
      
      await fetchShopAndStaff();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to verify background");
    }
  };

  const handleModalAction = (action) => {
    const { type, staffMember } = verificationModal;

    if (type === 'commission-required') {
      if (action === 'set-commission') {
        setVerificationModal({ isOpen: false, type: '', staffMember: null });
        openCommissionModal(staffMember);
      } else {
        // Cancel - close modal, no verification
        setVerificationModal({ isOpen: false, type: '', staffMember: null });
      }
    } else if (type === 'verify-confirm') {
      if (action === 'confirm') {
        proceedWithVerification(staffMember);
      } else {
        setVerificationModal({ isOpen: false, type: '', staffMember: null });
      }
    }
  };

  const handleToggleStaffStatus = async (staffMember) => {
    const token = localStorage.getItem("ogestyle_token");
    const newStatus = staffMember.status === "Active" ? "Inactive" : "Active";

    if (
      !window.confirm(
        `Are you sure you want to ${newStatus === "Inactive" ? "deactivate" : "reactivate"} ${staffMember.userId?.firstName}?`,
      )
    ) {
      return;
    }

    try {
      await axios.patch(
        `${API_BASE_URL}/api/shops/${shopId}/staff/${staffMember._id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      alert(
        `Staff ${newStatus === "Inactive" ? "deactivated" : "reactivated"} successfully!`,
      );
      await fetchShopAndStaff();
    } catch (error) {
      alert("Failed to update professional status");
    }
  };

  const viewStaffDetails = (staffMember) => {
    setSelectedStaffDetails(staffMember);
  };

  const getCommissionDisplay = (staffMember) => {
    if (!staffMember.commissionType || staffMember.commissionType === "none") {
      return (
        <span style={{ color: "#10b981", fontWeight: "600" }}>
          0% - Staff keeps 100%
        </span>
      );
    }

    if (staffMember.commissionType === "percentage") {
      return (
        <span style={{ color: "#3b82f6", fontWeight: "600" }}>
          {staffMember.commissionRate}% commission
          <span
            style={{ fontSize: "12px", color: "#6b7280", marginLeft: "8px" }}
          >
            (Staff gets {100 - staffMember.commissionRate}%)
          </span>
        </span>
      );
    }

    if (staffMember.commissionType === "flat") {
      return (
        <span style={{ color: "#8b5cf6", fontWeight: "600" }}>
          ${staffMember.flatCommissionAmount} per booking
        </span>
      );
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="dashboard-content">
          <h2>Professionals</h2>
          <p>Loading professionals...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="dashboard-content"
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        {/* Phase 3: Business Selector */}
        {businesses.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <BusinessSelector
              businesses={businesses}
              selectedBusinessId={selectedBusinessId}
              onBusinessChange={(id) => {
                setSelectedBusinessId(id);
                // Note: Full implementation would filter locations by business here
                // For now, just update the selected business
              }}
            />
          </div>
        )}

        {!isR2Manager && locations.length > 1 && (
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "13px",
                color: "#374151",
              }}
            >
              📍 Select Location
            </label>
            <select
              value={shopId || ""}
              onChange={(e) => {
                const newShopId = e.target.value;
                setShopId(newShopId);
                fetchShopAndStaff(newShopId);
              }}
              style={{
                width: "100%",
                maxWidth: "360px",
                padding: "10px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                minWidth: 0,
                cursor: "pointer",
              }}
            >
              {locations.map((location) => (
                <option key={location._id} value={location._id}>
                  {location.name} - {location.city}, {location.state}
                </option>
              ))}
            </select>
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            marginBottom: "24px",
            gap: isMobile ? "16px" : "12px",
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: isMobile ? "20px" : "24px",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            Professionals
          </h2>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              minWidth: 0,
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "stretch" : "flex-end",
            }}
          >
            <button
              className="btn-primary"
              style={{
                padding: "10px 20px",
                background: "#10b981",
                fontSize: "14px",
                flex: isMobile ? "1 1 auto" : "none",
              }}
              onClick={() => setShowAddModal(true)}
            >
              + Add Pro
            </button>
            <button
              className="btn-secondary"
              style={{
                padding: "10px 20px",
                background: "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                flex: isMobile ? "1 1 auto" : "none",
              }}
              onClick={() => setShowImportModal(true)}
            >
              📁 Import Professionals
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "20px",
            borderBottom: "2px solid #e5e7eb",
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <button
            onClick={() => setActiveTab("team")}
            style={{
              padding: "12px 16px",
              background: "none",
              border: "none",
              borderBottom: activeTab === "team" ? "3px solid #2563eb" : "none",
              color: activeTab === "team" ? "#2563eb" : "#6b7280",
              fontWeight: activeTab === "team" ? "600" : "400",
              fontSize: "14px",
              cursor: "pointer",
              marginBottom: "-2px",
            }}
          >
            👥 Professionals ({staff.length})
          </button>
          <button
            onClick={() => setActiveTab("schedule")}
            style={{
              padding: "12px 16px",
              background: "none",
              border: "none",
              borderBottom:
                activeTab === "schedule" ? "3px solid #2563eb" : "none",
              color: activeTab === "schedule" ? "#2563eb" : "#6b7280",
              fontWeight: activeTab === "schedule" ? "600" : "400",
              fontSize: "14px",
              cursor: "pointer",
              marginBottom: "-2px",
            }}
          >
            📅 Schedule Overview
          </button>
        </div>

        {/* Team Members Tab */}
        {activeTab === "team" && (
          <>
            {/* Unassigned Imported Staff Section */}
            {unassignedStaff.length > 0 && (
              <div
                className="dashboard-card"
                style={{
                  marginBottom: "24px",
                  border: "2px solid #fbbf24",
                  background:
                    "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 20px",
                    cursor: "pointer",
                    borderBottom: showUnassignedSection
                      ? "1px solid #fcd34d"
                      : "none",
                  }}
                  onClick={() =>
                    setShowUnassignedSection(!showUnassignedSection)
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span style={{ fontSize: "24px" }}>📥</span>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#92400e",
                        }}
                      >
                        Imported Professionals Pending Assignment
                      </h3>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "13px",
                          color: "#b45309",
                        }}
                      >
                        {unassignedStaff.length} professional
                        {unassignedStaff.length !== 1 ? "s" : ""} need to be
                        assigned to a location
                      </p>
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: "20px",
                      color: "#92400e",
                      transform: showUnassignedSection
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    ▼
                  </span>
                </div>

                {showUnassignedSection && (
                  <div style={{ padding: "16px 20px" }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: "12px",
                      }}
                    >
                      {unassignedStaff.map((staffMember) => (
                        <div
                          key={staffMember._id}
                          style={{
                            background: "white",
                            borderRadius: "12px",
                            padding: "16px",
                            border: "1px solid #e5e7eb",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "44px",
                              height: "44px",
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "16px",
                              fontWeight: "bold",
                              flexShrink: 0,
                            }}
                          >
                            {staffMember.userId?.firstName?.[0]}
                            {staffMember.userId?.lastName?.[0]}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: "600",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {staffMember.userId?.firstName}{" "}
                              {staffMember.userId?.lastName}
                            </p>
                            <p
                              style={{
                                margin: "2px 0 0 0",
                                fontSize: "12px",
                                color: "#6b7280",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {staffMember.userId?.email}
                            </p>
                          </div>
                          <button
                            onClick={() =>
                              handleAssignToLocation(staffMember._id)
                            }
                            disabled={assigningStaffId === staffMember._id}
                            style={{
                              padding: "8px 16px",
                              background:
                                assigningStaffId === staffMember._id
                                  ? "#d1d5db"
                                  : "#10b981",
                              color: "white",
                              border: "none",
                              borderRadius: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor:
                                assigningStaffId === staffMember._id
                                  ? "not-allowed"
                                  : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {assigningStaffId === staffMember._id
                              ? "Assigning..."
                              : "+ Assign Here"}
                          </button>
                        </div>
                      ))}
                    </div>
                    <p
                      style={{
                        margin: "12px 0 0 0",
                        fontSize: "12px",
                        color: "#92400e",
                        fontStyle: "italic",
                      }}
                    >
                      💡 Click "Assign Here" to add imported professionals to the
                      currently selected location
                    </p>
                  </div>
                )}
              </div>
            )}

            {staff.length === 0 ? (
              <div
                className="dashboard-card"
                style={{ textAlign: "center", padding: "40px" }}
              >
                <p style={{ color: "#6b7280", marginBottom: "16px" }}>
                  No professionals found
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  <button
                    className="btn-primary"
                    style={{ background: "#10b981" }}
                    onClick={() => setShowAddModal(true)}
                  >
                    + Add Pro
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {staff.map((staffMember) => (
                  <div
                    key={staffMember._id}
                    className="dashboard-card"
                    style={{
                      padding: "20px",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "20px",
                              fontWeight: "bold",
                            }}
                          >
                            {staffMember.userId?.firstName?.[0]}
                            {staffMember.userId?.lastName?.[0]}
                          </div>
                          <div>
                            <h3
                              style={{
                                margin: 0,
                                fontSize: "18px",
                                fontWeight: "600",
                              }}
                            >
                              {staffMember.userId?.firstName}{" "}
                              {staffMember.userId?.lastName}
                            </h3>
                            <p
                              style={{
                                margin: "4px 0 0 0",
                                color: "#6b7280",
                                fontSize: "14px",
                              }}
                            >
                              {formatRole(staffMember.role)}
                            </p>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isMobile
                              ? "1fr"
                              : "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: "16px",
                            marginBottom: "16px",
                            width: "100%",
                            minWidth: 0,
                          }}
                        >
                          <div>
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              Email
                            </p>
                            <p
                              style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                margin: 0,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {staffMember.userId?.email || "N/A"}
                            </p>
                          </div>
                          <div>
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              Phone
                            </p>
                            <p
                              style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                margin: 0,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {staffMember.userId?.phone || "N/A"}
                            </p>
                          </div>
                          <div>
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              Current Location
                            </p>
                            <p
                              style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                margin: 0,
                              }}
                            >
                              {(() => {
                                const currentShop = locations.find(
                                  (s) => s._id === shopId,
                                );
                                const staffLocation =
                                  staffMember.activeLocationId;
                                const isAtThisLocation =
                                  staffLocation === shopId ||
                                  staffLocation?._id === shopId;

                                return (
                                  <span
                                    style={{
                                      padding: "4px 12px",
                                      borderRadius: "12px",
                                      background: isAtThisLocation
                                        ? "#eff6ff"
                                        : "#f3f4f6",
                                      color: isAtThisLocation
                                        ? "#1e40af"
                                        : "#6b7280",
                                      fontSize: "13px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {isAtThisLocation
                                      ? `📍 ${currentShop?.city && currentShop?.state ? `${currentShop.city}, ${currentShop.state}` : currentShop?.name || "This Location"}`
                                      : `🏢 ${locations.length} ${locations.length === 1 ? "Location" : "Locations"}`}
                                  </span>
                                );
                              })()}
                            </p>
                          </div>
                          <div>
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              Status
                            </p>
                            <p
                              style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                margin: 0,
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  background:
                                    staffMember.status === "Active" &&
                                    staffMember.isActive === false
                                      ? "#fef3c7"
                                      : staffMember.status === "Active"
                                        ? "#ecfdf5"
                                        : "#fee2e2",
                                  color:
                                    staffMember.status === "Active" &&
                                    staffMember.isActive === false
                                      ? "#92400e"
                                      : staffMember.status === "Active"
                                        ? "#059669"
                                        : "#b91c1c",
                                  fontSize: "13px",
                                }}
                              >
                                {staffMember.status === "Active" &&
                                staffMember.isActive === false
                                  ? "⏳ Pending onboarding"
                                  : staffMember.status || "Active"}
                              </span>
                            </p>
                          </div>
                          <div>
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              Bank Account
                            </p>
                            <p
                              style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                margin: 0,
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  background:
                                    staffMember.hasBankAccount ||
                                    (staffMember.stripeConnectAccountId &&
                                      staffMember.stripePayoutsEnabled) ||
                                    (staffMember.bankAccount &&
                                      (staffMember.bankAccount.last4 ||
                                        staffMember.bankAccount.accountNumber))
                                      ? "#ecfdf5"
                                      : "#fef2f2",
                                  color:
                                    staffMember.hasBankAccount ||
                                    (staffMember.stripeConnectAccountId &&
                                      staffMember.stripePayoutsEnabled) ||
                                    (staffMember.bankAccount &&
                                      (staffMember.bankAccount.last4 ||
                                        staffMember.bankAccount.accountNumber))
                                      ? "#059669"
                                      : "#dc2626",
                                  fontSize: "13px",
                                }}
                              >
                                {staffMember.hasBankAccount ||
                                (staffMember.stripeConnectAccountId &&
                                  staffMember.stripePayoutsEnabled) ||
                                (staffMember.bankAccount &&
                                  (staffMember.bankAccount.last4 ||
                                    staffMember.bankAccount.accountNumber))
                                  ? `✓ Connected${staffMember.bankAccount?.last4 ? ` (****${staffMember.bankAccount.last4.slice(-4)})` : ""}`
                                  : "⚠ Not Set"}
                              </span>
                            </p>
                          </div>
                          {!staffMember.isOwner &&
                            staffMember.status !== "Archived" && (
                              <div>
                                <p
                                  style={{
                                    fontSize: "11px",
                                    color: "#6b7280",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Background Check
                                </p>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    margin: 0,
                                  }}
                                >
                                  <span
                                    style={{
                                      padding: "4px 12px",
                                      borderRadius: "12px",
                                      background: staffMember.backgroundVerified
                                        ? "#ecfdf5"
                                        : "#fef3c7",
                                      color: staffMember.backgroundVerified
                                        ? "#059669"
                                        : "#92400e",
                                      fontSize: "13px",
                                    }}
                                  >
                                    {staffMember.backgroundVerified
                                      ? "✓ Verified"
                                      : "⏳ Pending"}
                                  </span>
                                </p>
                              </div>
                            )}
                        </div>

                        {/* Revenue Split - Hidden for owners and R2 managers */}
                        {!staffMember.isOwner && !isR2Manager && (
                          <div
                            style={{
                              padding: "16px",
                              background: "#f9fafb",
                              borderRadius: "8px",
                              border: "1px solid #e5e7eb",
                              marginBottom: "16px",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              Revenue Split
                            </p>
                            <div
                              style={{ fontSize: "14px", marginBottom: "12px" }}
                            >
                              {getCommissionDisplay(staffMember)}
                            </div>
                            <button
                              onClick={() => openCommissionModal(staffMember)}
                              style={{
                                padding: "8px 16px",
                                background: "white",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: "500",
                              }}
                            >
                              ⚙️ Edit Split
                            </button>
                          </div>
                        )}

                        {/* Performance Metrics */}
                        {staffMetrics[staffMember._id] && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                  "repeat(auto-fit, minmax(130px, 1fr))",
                              gap: "12px",
                              marginBottom: "16px",
                              padding: "16px",
                              background: "#f0f9ff",
                              borderRadius: "8px",
                              border: "1px solid #bfdbfe",
                                width: "100%",
                                minWidth: 0,
                                boxSizing: "border-box",
                            }}
                          >
                            <div>
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#1e40af",
                                  marginBottom: "4px",
                                  textTransform: "uppercase",
                                  fontWeight: "600",
                                }}
                              >
                                Total Revenue
                              </p>
                              <p
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  margin: 0,
                                  color: "#1e3a8a",
                                }}
                              >
                                $
                                {staffMetrics[
                                  staffMember._id
                                ].totalRevenue?.toFixed(2) || "0.00"}
                              </p>
                            </div>
                            <div>
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#1e40af",
                                  marginBottom: "4px",
                                  textTransform: "uppercase",
                                  fontWeight: "600",
                                }}
                              >
                                Completed Bookings
                              </p>
                              <p
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  margin: 0,
                                  color: "#1e3a8a",
                                }}
                              >
                                {staffMetrics[staffMember._id]
                                  .completedBookings || 0}
                              </p>
                            </div>
                            <div>
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#1e40af",
                                  marginBottom: "4px",
                                  textTransform: "uppercase",
                                  fontWeight: "600",
                                }}
                              >
                                Upcoming Bookings
                              </p>
                              <p
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  margin: 0,
                                  color: "#1e3a8a",
                                }}
                              >
                                {staffMetrics[staffMember._id]
                                  .upcomingBookings || 0}
                              </p>
                            </div>
                            <div>
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#1e40af",
                                  marginBottom: "4px",
                                  textTransform: "uppercase",
                                  fontWeight: "600",
                                }}
                              >
                                Avg Rating
                              </p>
                              <p
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  margin: 0,
                                  color: "#1e3a8a",
                                }}
                              >
                                {staffMetrics[staffMember._id].averageRating
                                  ? `⭐ ${staffMetrics[staffMember._id].averageRating.toFixed(1)}`
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        {!isR2Manager && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: isMobile ? "column" : "row",
                              flexWrap: "wrap",
                              gap: "8px",
                              justifyContent: isMobile ? "stretch" : "flex-end",
                              marginTop: "8px",
                            }}
                          >
                          <button
                            onClick={() => viewStaffDetails(staffMember)}
                            style={{
                              padding: "8px 16px",
                              background: "#3b82f6",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "14px",
                              fontWeight: "500",
                            }}
                          >
                            📊 Details
                          </button>
                          {!staffMember.isOwner &&
                            !staffMember.userId?.verified && (
                              <button
                                onClick={() =>
                                  handleResendInvitation(staffMember)
                                }
                                style={{
                                  padding: "8px 16px",
                                  background: "#f3f4f6",
                                  color: "#374151",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                }}
                              >
                                ✉️ Resend
                              </button>
                            )}
                          {!staffMember.isOwner &&
                            !staffMember.backgroundVerified &&
                            staffMember.status !== "Archived" && (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <button
                                  onClick={() =>
                                    handleVerifyBackground(staffMember)
                                  }
                                  style={{
                                    padding: "8px 16px",
                                    background: "#10b981",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                  }}
                                >
                                  ✓ Verify Background
                                </button>
                                {(!staffMember.commissionType || staffMember.commissionType === "none") && (
                                  <span
                                    style={{
                                      padding: "4px 8px",
                                      background: "#fef3c7",
                                      color: "#92400e",
                                      border: "1px solid #f59e0b",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      whiteSpace: "nowrap",
                                    }}
                                    title="Revenue split is REQUIRED before verification can proceed"
                                  >
                                    ⚠️ Split required
                                  </span>
                                )}
                              </div>
                            )}
                          {!staffMember.isOwner &&
                            staffMember.status !== "Archived" && (
                              <button
                                onClick={() =>
                                  handleToggleStaffStatus(staffMember)
                                }
                                style={{
                                  padding: "8px 16px",
                                  background:
                                    staffMember.status === "Active"
                                      ? "#fef3c7"
                                      : "#ecfdf5",
                                  color:
                                    staffMember.status === "Active"
                                      ? "#92400e"
                                      : "#059669",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                }}
                              >
                                {staffMember.status === "Active"
                                  ? "Deactivate"
                                  : "Activate"}
                              </button>
                            )}
                          {/* DISABLED: Move to location feature
                          Future: Will be replaced with day-based location scheduling
                          where professionals can set different locations for different days of the week
                      {!staffMember.isOwner && locations.length > 1 && (
                        <button
                          onClick={() => {
                            setSelectedStaffToMove(staffMember);
                            setShowMoveModal(true);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          📍 Add to Location
                        </button>
                      )}
                      */}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pending Invitations Section */}
            {pendingInvitations.length > 0 && (
              <div style={{ marginTop: "32px" }}>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginBottom: "16px",
                    color: "#111827",
                  }}
                >
                  ⏳ Pending Invitations ({pendingInvitations.length})
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {pendingInvitations.map((invite) => (
                    <div
                      key={invite._id}
                      className="dashboard-card"
                      style={{
                        padding: "16px",
                        background: "#fffbeb",
                        borderLeft: "4px solid #f59e0b",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "4px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#111827",
                              }}
                            >
                              {invite.firstName} {invite.lastName}
                            </span>
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: "12px",
                                background: "#fef3c7",
                                color: "#92400e",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              PENDING
                            </span>
                          </div>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "#6b7280",
                              margin: "0 0 8px 0",
                            }}
                          >
                            📧 {invite.email} · {formatRole(invite.role)}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              gap: "16px",
                              fontSize: "12px",
                              color: "#92400e",
                            }}
                          >
                            <span>
                              Invited:{" "}
                              {new Date(invite.invitedAt).toLocaleDateString()}
                            </span>
                            <span style={{ fontWeight: "600" }}>
                              ⏰ Expires in {invite.hoursRemaining}h
                            </span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            onClick={async () => {
                              if (
                                window.confirm(
                                  `Cancel invitation for ${invite.email}?`,
                                )
                              ) {
                                try {
                                  const token =
                                    localStorage.getItem("ogestyle_token");
                                  await axios.delete(
                                    `${API_BASE_URL}/api/shops/${shopId}/staff/invitations/${invite._id}`,
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    },
                                  );
                                  alert("Invitation cancelled");
                                  await fetchShopAndStaff(); // Refresh the list
                                } catch (err) {
                                  alert(
                                    err.response?.data?.message ||
                                      "Failed to cancel invitation",
                                  );
                                }
                              }
                            }}
                            style={{
                              padding: "8px 16px",
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            ❌ Cancel
                          </button>
                          <button
                            onClick={async () => {
                              if (
                                window.confirm(
                                  `Resend invitation to ${invite.email}?`,
                                )
                              ) {
                                try {
                                  const token =
                                    localStorage.getItem("ogestyle_token");
                                  // Delete old invitation first
                                  await axios.delete(
                                    `${API_BASE_URL}/api/shops/${shopId}/staff/invitations/${invite._id}`,
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    },
                                  );
                                  // Send new invitation (include roleCode from the original invite)
                                  await axios.post(
                                    `${API_BASE_URL}/api/shops/${shopId}/staff/invite`,
                                    {
                                      email: invite.email,
                                      firstName: invite.firstName,
                                      lastName: invite.lastName,
                                      professionalTypes:
                                        invite.professionalTypes || [
                                          invite.role,
                                        ], // Use array or convert legacy single role
                                      primaryProfessionalType:
                                        invite.primaryProfessionalType ||
                                        invite.role, // Use primary or legacy role
                                      roleCode: invite.roleCode || "R3", // Use stored roleCode or default to R3
                                      locationId: shopId,
                                    },
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    },
                                  );
                                  alert("Invitation resent!");
                                  await fetchShopAndStaff(); // Refresh the list
                                } catch (err) {
                                  alert(
                                    err.response?.data?.message ||
                                      "Failed to resend invitation",
                                  );
                                }
                              }
                            }}
                            style={{
                              padding: "8px 16px",
                              background: "#3b82f6",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            🔄 Resend
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Move to Location Modal */}
            {showMoveModal && selectedStaffToMove && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "32px",
                    maxWidth: "500px",
                    width: "90%",
                  }}
                >
                  <h3 style={{ marginTop: 0, marginBottom: "24px" }}>
                    Move {selectedStaffToMove.userId.firstName} to Another
                    Location
                  </h3>

                  <div style={{ marginBottom: "24px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Select Location
                    </label>
                    <select
                      value={targetLocationId}
                      onChange={(e) => setTargetLocationId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    >
                      <option value="">Choose a location...</option>
                      {locations
                        .filter((loc) => {
                          // Filter out current location
                          const currentLocationId =
                            selectedStaffToMove.activeLocationId?._id ||
                            selectedStaffToMove.activeLocationId;
                          return loc._id !== currentLocationId;
                        })
                        .map((location) => (
                          <option key={location._id} value={location._id}>
                            {location.name} - {location.city}, {location.state}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div
                    style={{
                      padding: "16px",
                      background: "#fef3c7",
                      border: "1px solid #f59e0b",
                      borderRadius: "8px",
                      marginBottom: "24px",
                    }}
                  >
                    <p
                      style={{ fontSize: "13px", color: "#92400e", margin: 0 }}
                    >
                      ⚠️ This will move the professional from their current
                      location to the selected location.
                    </p>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() => {
                        setShowMoveModal(false);
                        setSelectedStaffToMove(null);
                        setTargetLocationId("");
                      }}
                      style={{
                        padding: "10px 20px",
                        background: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleMoveToLocation}
                      disabled={!targetLocationId}
                      style={{
                        padding: "10px 20px",
                        background: targetLocationId ? "#3b82f6" : "#d1d5db",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: targetLocationId ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Move to Location
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Add Pro Modal */}
            {showAddModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "32px",
                    maxWidth: "500px",
                    width: "90%",
                    maxHeight: "90vh",
                    overflow: "auto",
                  }}
                >
                  <h3 style={{ marginTop: 0, marginBottom: "24px" }}>
                    Add New Professional
                  </h3>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={addForm.firstName}
                      onChange={(e) =>
                        setAddForm({ ...addForm, firstName: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                      placeholder="Enter first name"
                    />
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={addForm.lastName}
                      onChange={(e) =>
                        setAddForm({ ...addForm, lastName: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                      placeholder="Enter last name"
                    />
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={addForm.email}
                      onChange={(e) =>
                        setAddForm({ ...addForm, email: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                      placeholder="professional@example.com"
                    />
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        margin: "4px 0 0 0",
                      }}
                    >
                      Email cannot be changed later
                    </p>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      value={addForm.phone}
                      onChange={(e) =>
                        setAddForm({ ...addForm, phone: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                      placeholder="+1234567890"
                    />
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "12px",
                        fontWeight: "600",
                      }}
                    >
                      Professional Types * (Select 1-5)
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: "10px",
                        maxHeight: "250px",
                        overflow: "auto",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        background: "#f9fafb",
                      }}
                    >
                      {getFilteredProfessionalTypes().map((type) => (
                        <label
                          key={type.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px",
                            background: addForm.professionalTypes.includes(
                              type.value,
                            )
                              ? "#d1fae5"
                              : "white",
                            border: `1px solid ${addForm.professionalTypes.includes(type.value) ? "#10b981" : "#e5e7eb"}`,
                            borderRadius: "6px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              addForm.professionalTypes.includes(type.value)
                                ? "#a7f3d0"
                                : "#f3f4f6")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              addForm.professionalTypes.includes(type.value)
                                ? "#d1fae5"
                                : "white")
                          }
                        >
                          <input
                            type="checkbox"
                            checked={addForm.professionalTypes.includes(
                              type.value,
                            )}
                            onChange={(e) => {
                              const newTypes = e.target.checked
                                ? [...addForm.professionalTypes, type.value]
                                : addForm.professionalTypes.filter(
                                    (t) => t !== type.value,
                                  );

                              // If unchecking the primary type, clear it
                              const newPrimary = newTypes.includes(
                                addForm.primaryProfessionalType,
                              )
                                ? addForm.primaryProfessionalType
                                : newTypes.length > 0
                                  ? newTypes[0]
                                  : "";

                              setAddForm({
                                ...addForm,
                                professionalTypes: newTypes,
                                primaryProfessionalType: newPrimary,
                              });
                            }}
                            style={{ marginRight: "8px", cursor: "pointer" }}
                          />
                          <span style={{ fontSize: "14px", flex: 1 }}>
                            {type.icon && `${type.icon} `}
                            {type.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        margin: "4px 0 0 0",
                      }}
                    >
                      {addForm.professionalTypes.length}/5 selected
                    </p>
                  </div>

                  {addForm.professionalTypes.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "12px",
                          fontWeight: "600",
                        }}
                      >
                        Primary Professional Type *
                      </label>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr",
                          gap: "8px",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          background: "#f9fafb",
                        }}
                      >
                        {addForm.professionalTypes.map((typeValue) => {
                          const type = professionalTypes.find(
                            (t) => t.value === typeValue,
                          );
                          return (
                            <label
                              key={typeValue}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "8px",
                                background:
                                  addForm.primaryProfessionalType === typeValue
                                    ? "#d1fae5"
                                    : "white",
                                border: `1px solid ${addForm.primaryProfessionalType === typeValue ? "#10b981" : "#e5e7eb"}`,
                                borderRadius: "6px",
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="radio"
                                name="addPrimaryType"
                                checked={
                                  addForm.primaryProfessionalType === typeValue
                                }
                                onChange={() =>
                                  setAddForm({
                                    ...addForm,
                                    primaryProfessionalType: typeValue,
                                  })
                                }
                                style={{
                                  marginRight: "8px",
                                  cursor: "pointer",
                                }}
                              />
                              <span style={{ fontSize: "14px" }}>
                                {type?.icon && `${type.icon} `}
                                {type?.label || typeValue}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          margin: "4px 0 0 0",
                        }}
                      >
                        This will be their main specialty shown on their profile
                      </p>
                    </div>
                  )}

                  <div style={{ marginBottom: "24px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Location *
                    </label>
                    <select
                      value={addForm.locationId}
                      onChange={(e) =>
                        setAddForm({ ...addForm, locationId: e.target.value })
                      }
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    >
                      <option value="">Select location...</option>
                      {locations.map((location) => (
                        <option key={location._id} value={location._id}>
                          {location.name} - {location.city}, {location.state}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    style={{
                      padding: "16px",
                      background: "#ecfdf5",
                      border: "1px solid #10b981",
                      borderRadius: "8px",
                      marginBottom: "24px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#065f46",
                        margin: 0,
                        fontWeight: "500",
                      }}
                    >
                      ✓ Account will be created immediately. An email will be
                      sent with a magic link to set their password.
                    </p>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() => {
                        setShowAddModal(false);
                        setAddForm({
                          email: "",
                          professionalTypes: [],
                          primaryProfessionalType: "",
                          firstName: "",
                          lastName: "",
                          phone: "",
                          locationId: "",
                        });
                      }}
                      style={{
                        padding: "10px 20px",
                        background: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddStaff}
                      style={{
                        padding: "10px 20px",
                        background: "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Add Professional
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Staff Details Modal */}
            {selectedStaffDetails && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  overflow: "auto",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "32px",
                    maxWidth: "800px",
                    width: "100%",
                    maxHeight: "90vh",
                    overflow: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                      marginBottom: "24px",
                    }}
                  >
                    <div>
                      <h3 style={{ margin: 0, fontSize: "24px" }}>
                        {selectedStaffDetails.userId?.firstName}{" "}
                        {selectedStaffDetails.userId?.lastName}
                      </h3>
                      <p
                        style={{
                          margin: "8px 0 0 0",
                          color: "#6b7280",
                          fontSize: "16px",
                        }}
                      >
                        Professional
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedStaffDetails(null);
                        setShowEmailChange(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#6b7280",
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Performance Overview */}
                  {staffMetrics[selectedStaffDetails._id] && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: "16px",
                        marginBottom: "24px",
                      }}
                    >
                      <div
                        style={{
                          padding: "20px",
                          background:
                            "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                          borderRadius: "12px",
                          color: "white",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "12px",
                            marginBottom: "8px",
                            opacity: 0.9,
                          }}
                        >
                          TOTAL REVENUE
                        </p>
                        <p
                          style={{
                            fontSize: "28px",
                            fontWeight: "700",
                            margin: 0,
                          }}
                        >
                          $
                          {staffMetrics[
                            selectedStaffDetails._id
                          ].totalRevenue?.toFixed(2) || "0.00"}
                        </p>
                      </div>
                      <div
                        style={{
                          padding: "20px",
                          background:
                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                          borderRadius: "12px",
                          color: "white",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "12px",
                            marginBottom: "8px",
                            opacity: 0.9,
                          }}
                        >
                          COMPLETED
                        </p>
                        <p
                          style={{
                            fontSize: "28px",
                            fontWeight: "700",
                            margin: 0,
                          }}
                        >
                          {staffMetrics[selectedStaffDetails._id]
                            .completedBookings || 0}
                        </p>
                        <p
                          style={{
                            fontSize: "11px",
                            marginTop: "4px",
                            opacity: 0.8,
                          }}
                        >
                          bookings
                        </p>
                      </div>
                      <div
                        style={{
                          padding: "20px",
                          background:
                            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                          borderRadius: "12px",
                          color: "white",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "12px",
                            marginBottom: "8px",
                            opacity: 0.9,
                          }}
                        >
                          UPCOMING
                        </p>
                        <p
                          style={{
                            fontSize: "28px",
                            fontWeight: "700",
                            margin: 0,
                          }}
                        >
                          {staffMetrics[selectedStaffDetails._id]
                            .upcomingBookings || 0}
                        </p>
                        <p
                          style={{
                            fontSize: "11px",
                            marginTop: "4px",
                            opacity: 0.8,
                          }}
                        >
                          bookings
                        </p>
                      </div>
                      <div
                        style={{
                          padding: "20px",
                          background:
                            "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                          borderRadius: "12px",
                          color: "white",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "12px",
                            marginBottom: "8px",
                            opacity: 0.9,
                          }}
                        >
                          AVG RATING
                        </p>
                        <p
                          style={{
                            fontSize: "28px",
                            fontWeight: "700",
                            margin: 0,
                          }}
                        >
                          {staffMetrics[selectedStaffDetails._id].averageRating
                            ? `⭐ ${staffMetrics[selectedStaffDetails._id].averageRating.toFixed(1)}`
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Contact & Account Info */}
                  <div
                    style={{
                      padding: "20px",
                      background: "#f9fafb",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      marginBottom: "24px",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        fontSize: "16px",
                      }}
                    >
                      Contact & Account
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          Email
                        </p>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              margin: 0,
                            }}
                          >
                            {selectedStaffDetails.userId?.email || "N/A"}
                          </p>
                          {!showEmailChange && (
                            <button
                              onClick={() => setShowEmailChange(true)}
                              style={{
                                padding: "4px 12px",
                                background: "#00509d",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: "500",
                              }}
                            >
                              Change
                            </button>
                          )}
                        </div>
                        {showEmailChange && (
                          <AdminEmailChange
                            staffId={selectedStaffDetails._id}
                            currentEmail={
                              selectedStaffDetails.userId?.email || ""
                            }
                            onSuccess={(newEmail) => {
                              // Update the selectedStaffDetails with new email
                              setSelectedStaffDetails({
                                ...selectedStaffDetails,
                                userId: {
                                  ...selectedStaffDetails.userId,
                                  email: newEmail,
                                },
                              });
                              // Update in the staff list
                              setStaff((prev) =>
                                prev.map((s) =>
                                  s._id === selectedStaffDetails._id
                                    ? {
                                        ...s,
                                        userId: {
                                          ...s.userId,
                                          email: newEmail,
                                        },
                                      }
                                    : s,
                                ),
                              );
                              setShowEmailChange(false);
                              alert(
                                `Email changed successfully to ${newEmail}`,
                              );
                            }}
                            onCancel={() => setShowEmailChange(false)}
                          />
                        )}
                      </div>
                      <div>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          Phone
                        </p>
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            margin: 0,
                          }}
                        >
                          {selectedStaffDetails.userId?.phone || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          Bank Account
                        </p>
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            margin: 0,
                          }}
                        >
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: "12px",
                              background:
                                selectedStaffDetails.hasBankAccount ||
                                (selectedStaffDetails.stripeConnectAccountId &&
                                  selectedStaffDetails.stripePayoutsEnabled) ||
                                (selectedStaffDetails.bankAccount &&
                                  (selectedStaffDetails.bankAccount.last4 ||
                                    selectedStaffDetails.bankAccount
                                      .accountNumber))
                                  ? "#ecfdf5"
                                  : "#fef2f2",
                              color:
                                selectedStaffDetails.hasBankAccount ||
                                (selectedStaffDetails.stripeConnectAccountId &&
                                  selectedStaffDetails.stripePayoutsEnabled) ||
                                (selectedStaffDetails.bankAccount &&
                                  (selectedStaffDetails.bankAccount.last4 ||
                                    selectedStaffDetails.bankAccount
                                      .accountNumber))
                                  ? "#059669"
                                  : "#dc2626",
                              fontSize: "13px",
                            }}
                          >
                            {selectedStaffDetails.hasBankAccount ||
                            (selectedStaffDetails.stripeConnectAccountId &&
                              selectedStaffDetails.stripePayoutsEnabled) ||
                            (selectedStaffDetails.bankAccount &&
                              (selectedStaffDetails.bankAccount.last4 ||
                                selectedStaffDetails.bankAccount.accountNumber))
                              ? `✓ Connected${selectedStaffDetails.bankAccount?.last4 ? ` (****${selectedStaffDetails.bankAccount.last4.slice(-4)})` : ""}`
                              : "⚠ Not Connected"}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          Account Status
                        </p>
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            margin: 0,
                          }}
                        >
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: "12px",
                              background:
                                selectedStaffDetails.status === "Active"
                                  ? "#ecfdf5"
                                  : "#fef3c7",
                              color:
                                selectedStaffDetails.status === "Active"
                                  ? "#059669"
                                  : "#92400e",
                              fontSize: "13px",
                            }}
                          >
                            {selectedStaffDetails.status || "Active"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Commission Info */}
                  <div
                    style={{
                      padding: "20px",
                      background: "#f0f9ff",
                      borderRadius: "8px",
                      border: "1px solid #bfdbfe",
                      marginBottom: "24px",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "12px",
                        fontSize: "16px",
                      }}
                    >
                      Revenue Split
                    </h4>
                    {getCommissionDisplay(selectedStaffDetails)}
                    {selectedStaffDetails.isOwner && (
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#059669",
                          marginTop: "8px",
                          fontStyle: "italic",
                        }}
                      >
                        💼 As the owner, this staff member's earnings are
                        automatically credited to the shop account.
                      </p>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedStaffDetails(null);
                        setShowEmailChange(false);
                        openCommissionModal(selectedStaffDetails);
                      }}
                      style={{
                        padding: "10px 20px",
                        background: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Edit Commission
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStaffDetails(null);
                        setShowEmailChange(false);
                      }}
                      style={{
                        padding: "10px 20px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Commission Edit Modal */}
            {editingStaff && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "32px",
                    maxWidth: "500px",
                    width: "90%",
                    maxHeight: "90vh",
                    overflow: "auto",
                  }}
                >
                  <h3 style={{ marginTop: 0, marginBottom: "24px" }}>
                    Edit Revenue Split - {editingStaff.userId?.firstName}{" "}
                    {editingStaff.userId?.lastName}
                  </h3>

                  <div style={{ marginBottom: "24px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Commission Type
                    </label>
                    <select
                      value={commissionForm.commissionType}
                      onChange={(e) =>
                        setCommissionForm({
                          ...commissionForm,
                          commissionType: e.target.value,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    >
                      <option value="none">
                        No Commission - Staff keeps 100%
                      </option>
                      <option value="percentage">
                        Percentage of Service Revenue
                      </option>
                      <option value="flat">Flat Fee per Booking</option>
                    </select>
                  </div>

                  {commissionForm.commissionType === "percentage" && (
                    <div style={{ marginBottom: "24px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                        }}
                      >
                        Shop Commission Rate (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={commissionForm.commissionRate}
                        onChange={(e) =>
                          setCommissionForm({
                            ...commissionForm,
                            commissionRate: parseFloat(e.target.value) || 0,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginTop: "8px",
                        }}
                      >
                        Staff will receive{" "}
                        {100 - (commissionForm.commissionRate || 0)}% of service
                        revenue
                      </p>
                    </div>
                  )}

                  {commissionForm.commissionType === "flat" && (
                    <div style={{ marginBottom: "24px" }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                        }}
                      >
                        Flat Commission per Booking ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={commissionForm.flatCommissionAmount}
                        onChange={(e) =>
                          setCommissionForm({
                            ...commissionForm,
                            flatCommissionAmount:
                              parseFloat(e.target.value) || 0,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginTop: "8px",
                        }}
                      >
                        Shop will receive $
                        {commissionForm.flatCommissionAmount || 0} per completed
                        booking
                      </p>
                    </div>
                  )}

                  <div
                    style={{
                      padding: "16px",
                      background: "#eff6ff",
                      border: "1px solid #3b82f6",
                      borderRadius: "8px",
                      marginBottom: "24px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#1e40af",
                        margin: 0,
                        fontWeight: "500",
                      }}
                    >
                      ℹ️ Tips are always 100% to the staff member
                    </p>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={closeCommissionModal}
                      style={{
                        padding: "10px 20px",
                        background: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCommissionUpdate}
                      style={{
                        padding: "10px 20px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Schedule Overview Tab */}
        {activeTab === "schedule" && (
          <div style={{ marginTop: "20px" }}>
            {loadingSchedule ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#6b7280",
                }}
              >
                Loading schedule overview...
              </div>
            ) : scheduleData.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  background: "#f9fafb",
                  borderRadius: "12px",
                  border: "2px dashed #d1d5db",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>📅</div>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  No Schedules Found
                </h3>
                <p style={{ color: "#6b7280", fontSize: "14px" }}>
                  Staff members need to set up their schedules first
                </p>
              </div>
            ) : (
              <>
                {/* Week Navigator */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                    padding: "16px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                  }}
                >
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(newDate.getDate() - 7);
                      setSelectedDate(newDate);
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "white",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    ← Previous Week
                  </button>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "16px",
                      color: "#374151",
                    }}
                  >
                    Week of{" "}
                    {selectedDate.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <button
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(newDate.getDate() + 7);
                      setSelectedDate(newDate);
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "white",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Next Week →
                  </button>
                </div>

                {/* Schedule Grid */}
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      background: "white",
                      borderRadius: "8px",
                      overflow: "hidden",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "#f9fafb",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#374151",
                            position: "sticky",
                            left: 0,
                            background: "#f9fafb",
                            zIndex: 10,
                          }}
                        >
                          Professional
                        </th>
                        {(() => {
                          const days = [
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                            "Sun",
                          ];
                          const startOfWeek = new Date(selectedDate);
                          startOfWeek.setDate(
                            startOfWeek.getDate() - startOfWeek.getDay() + 1,
                          ); // Start on Monday

                          return days.map((day, idx) => {
                            const currentDate = new Date(startOfWeek);
                            currentDate.setDate(currentDate.getDate() + idx);
                            const dateStr = currentDate.toLocaleDateString(
                              "en-US",
                              { month: "short", day: "numeric" },
                            );

                            return (
                              <th
                                key={idx}
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  color: "#374151",
                                  minWidth: "120px",
                                }}
                              >
                                <div>{day}</div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "400",
                                    color: "#6b7280",
                                  }}
                                >
                                  {dateStr}
                                </div>
                              </th>
                            );
                          });
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {scheduleData.map((staffSchedule, idx) => (
                        <tr
                          key={idx}
                          style={{ borderBottom: "1px solid #e5e7eb" }}
                        >
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "500",
                              fontSize: "14px",
                              position: "sticky",
                              left: 0,
                              background: "white",
                              zIndex: 5,
                            }}
                          >
                            <div>{staffSchedule.name}</div>
                            <div style={{ fontSize: "12px", color: "#6b7280" }}>
                              {staffSchedule.professionalType}
                            </div>
                          </td>
                          {[
                            "monday",
                            "tuesday",
                            "wednesday",
                            "thursday",
                            "friday",
                            "saturday",
                            "sunday",
                          ].map((day) => {
                            const daySchedule =
                              staffSchedule.weeklySchedule?.[day];

                            // Handle both formats: new multi-block and legacy
                            let isAvailable = false;
                            let blocks = [];

                            if (daySchedule) {
                              // New format: { isAvailable: true/false, blocks: [...] }
                              if (daySchedule.isAvailable !== undefined) {
                                isAvailable = daySchedule.isAvailable;
                                blocks = daySchedule.blocks || [];
                              }
                              // Legacy format: { active: true/false, open: "09:00", close: "17:00" }
                              else if (daySchedule.active !== undefined) {
                                isAvailable = daySchedule.active;
                                if (
                                  isAvailable &&
                                  daySchedule.open &&
                                  daySchedule.close
                                ) {
                                  blocks = [
                                    {
                                      start: daySchedule.open,
                                      end: daySchedule.close,
                                    },
                                  ];
                                }
                              }
                              // Direct blocks array (some formats)
                              else if (Array.isArray(daySchedule)) {
                                isAvailable = daySchedule.length > 0;
                                blocks = daySchedule;
                              }
                            }

                            return (
                              <td
                                key={day}
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  fontSize: "12px",
                                  background: isAvailable
                                    ? "#f0fdf4"
                                    : "#fef2f2",
                                }}
                              >
                                {!isAvailable ? (
                                  <div
                                    style={{
                                      color: "#991b1b",
                                      fontWeight: "500",
                                    }}
                                  >
                                    Closed
                                  </div>
                                ) : blocks.length === 0 ? (
                                  <div style={{ color: "#6b7280" }}>
                                    Available
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "4px",
                                    }}
                                  >
                                    {blocks.map((block, blockIdx) => (
                                      <div
                                        key={blockIdx}
                                        style={{
                                          background: "#10b981",
                                          color: "white",
                                          padding: "4px 8px",
                                          borderRadius: "4px",
                                          fontSize: "11px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        {block.start} - {block.end}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div
                  style={{
                    marginTop: "20px",
                    padding: "16px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                    display: "flex",
                    gap: "24px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        background: "#f0fdf4",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                      }}
                    ></div>
                    <span style={{ fontSize: "13px", color: "#374151" }}>
                      Working
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        background: "#fef2f2",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                      }}
                    ></div>
                    <span style={{ fontSize: "13px", color: "#374151" }}>
                      Not Available
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "16px",
                        background: "#10b981",
                        borderRadius: "4px",
                      }}
                    ></div>
                    <span style={{ fontSize: "13px", color: "#374151" }}>
                      Time Block
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Location Selector Modal for Staff Assignment */}
        {showLocationSelector && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                Select Location to Assign Staff
              </h3>
              <p
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "14px",
                  color: "#6b7280",
                }}
              >
                Choose which location this staff member should be assigned to:
              </p>
              <select
                value={selectedLocationForAssignment}
                onChange={(e) =>
                  setSelectedLocationForAssignment(e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">-- Select a location --</option>
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    {loc.name} - {loc.city}, {loc.state}
                  </option>
                ))}
              </select>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => {
                    setShowLocationSelector(false);
                    setStaffToAssign(null);
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAssignToLocation}
                  disabled={!selectedLocationForAssignment || assigningStaffId}
                  style={{
                    padding: "10px 20px",
                    background: selectedLocationForAssignment
                      ? "#10b981"
                      : "#d1d5db",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: selectedLocationForAssignment
                      ? "pointer"
                      : "not-allowed",
                  }}
                >
                  {assigningStaffId ? "Assigning..." : "Assign to Location"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Staff Import Modal */}
        <StaffImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            fetchShopAndStaff();
          }}
          businessId={selectedBusinessId || businesses[0]?._id}
        />

        {/* Custom Verification Modal */}
        {verificationModal.isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
              animation: "fadeIn 0.2s ease-out",
            }}
            onClick={() => handleModalAction('cancel')}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                maxWidth: "520px",
                width: "90%",
                animation: "slideUp 0.3s ease-out",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Commission Required Modal - NO OVERRIDE */}
              {verificationModal.type === 'commission-required' && (
                <>
                  <div
                    style={{
                      padding: "32px 32px 24px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "20px",
                      }}
                    >
                      <span style={{ fontSize: "28px" }}>⚠️</span>
                    </div>
                    <h3
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#111827",
                        lineHeight: "1.2",
                      }}
                    >
                      Revenue Split Required
                    </h3>
                    <p
                      style={{
                        margin: "0",
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#6b7280",
                      }}
                    >
                      <strong style={{ color: "#374151" }}>
                        {verificationModal.staffMember?.userId.firstName}{" "}
                        {verificationModal.staffMember?.userId.lastName}
                      </strong>{" "}
                      must have a revenue split configured before verification.
                    </p>
                  </div>
                  <div style={{ padding: "24px 32px" }}>
                    <div
                      style={{
                        padding: "16px",
                        background: "#f0fdf4",
                        border: "1px solid #86efac",
                        borderRadius: "10px",
                        marginBottom: "24px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "start",
                        }}
                      >
                        <span style={{ fontSize: "20px", marginTop: "2px" }}>💡</span>
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              margin: "0 0 8px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#166534",
                            }}
                          >
                            Why is this required?
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "13px",
                              lineHeight: "1.5",
                              color: "#15803d",
                            }}
                          >
                            The revenue split must be set <strong>before verification</strong>{" "}
                            to ensure accurate earnings calculation from day one and prevent
                            any retroactive calculation issues or payment disputes.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        flexDirection: "column-reverse",
                      }}
                    >
                      <button
                        onClick={() => handleModalAction('cancel')}
                        style={{
                          padding: "14px 24px",
                          background: "#f3f4f6",
                          color: "#6b7280",
                          border: "none",
                          borderRadius: "10px",
                          fontSize: "15px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = "#e5e7eb";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = "#f3f4f6";
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleModalAction('set-commission')}
                        style={{
                          padding: "14px 24px",
                          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          color: "white",
                          border: "none",
                          borderRadius: "10px",
                          fontSize: "15px",
                          fontWeight: "600",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow = "0 6px 16px rgba(16, 185, 129, 0.4)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.3)";
                        }}
                      >
                        ✓ Set Revenue Split Now
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Final Verification Confirmation Modal */}
              {verificationModal.type === 'verify-confirm' && (
                <>
                  <div
                    style={{
                      padding: "32px 32px 24px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "20px",
                      }}
                    >
                      <span style={{ fontSize: "28px" }}>✓</span>
                    </div>
                    <h3
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#111827",
                        lineHeight: "1.2",
                      }}
                    >
                      Verify Background Check
                    </h3>
                    <p
                      style={{
                        margin: "0",
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#6b7280",
                      }}
                    >
                      Mark{" "}
                      <strong style={{ color: "#374151" }}>
                        {verificationModal.staffMember?.userId.firstName}{" "}
                        {verificationModal.staffMember?.userId.lastName}
                      </strong>{" "}
                      as background verified?
                    </p>
                  </div>
                  <div style={{ padding: "24px 32px" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        flexDirection: "column-reverse",
                      }}
                    >
                      <button
                        onClick={() => handleModalAction('cancel')}
                        style={{
                          padding: "14px 24px",
                          background: "#f3f4f6",
                          color: "#374151",
                          border: "none",
                          borderRadius: "10px",
                          fontSize: "15px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = "#e5e7eb";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = "#f3f4f6";
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleModalAction('confirm')}
                        style={{
                          padding: "14px 24px",
                          background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                          color: "white",
                          border: "none",
                          borderRadius: "10px",
                          fontSize: "15px",
                          fontWeight: "600",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
                        }}
                      >
                        ✓ Verify Background
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default Staff;
